import { useState, useEffect, lazy, Suspense } from 'react'
import { apiFetch, authedFetch } from './utils/shopifyFetch'
import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom'
import ShopifyNavMenu from './components/ShopifyNavMenu'
import Layout from './components/Layout'
import RouteGuard from './components/RouteGuard'
import { NavigationProvider } from './context/NavigationContext'
import { LangProvider } from './context/LangContext'
import ToastContainer from './components/ToastContainer'
import { useToast } from './hooks/useToast'
// Capture host ONCE at module load — window.location.search changes after
// React Router navigates, but we need a stable value for the lifetime of the page.
// Evaluated on import, so the same rule applies as in utils/shopifyHost.
const INITIAL_HOST = new URLSearchParams(window.location.search).get('host') ||
  readSession('shopify_host', '') || ''
import OnboardingFlow from './pages/OnboardingFlow'
import Dashboard from './pages/Dashboard'
import ErrorBoundary from './components/ErrorBoundary'
/**
 * Everything past the first screen, fetched when it is asked for.
 *
 * The whole admin was one 488 KB chunk, so a merchant opening the dashboard
 * downloaded the product table, the order list, the upload flow and the
 * settings pages before seeing anything — and Shopify measures what an
 * embedded app costs to open. The dashboard and onboarding stay eager, because
 * they are what the first paint actually needs.
 */
const Products      = lazy(() => import('./pages/Products'))
const ProductDetail = lazy(() => import('./pages/ProductDetail'))
const Orders        = lazy(() => import('./pages/Orders'))
const UploadFlow    = lazy(() => import('./pages/UploadFlow'))
const Activity      = lazy(() => import('./pages/Activity'))
const Pricing       = lazy(() => import('./pages/Pricing'))
const Settings      = lazy(() => import('./pages/Settings'))
const Help          = lazy(() => import('./pages/Help'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const TermsOfService= lazy(() => import('./pages/TermsOfService'))
const NotFound      = lazy(() => import('./pages/NotFound'))
const Profiling     = lazy(() => import('./pages/Profiling'))
const ProfilingEditor = lazy(() =>
  import('./pages/Profiling').then(m => ({ default: m.ProfilingEditor })))

import OpenInShopify from './components/OpenInShopify'
import { isEmbeddedContext } from './utils/embedded'
import TawkChat from './components/TawkChat'
import { readStored, writeStored, removeStored, readSession } from './utils/safeStorage'
import { isAdminView, adminImpersonationShop } from './utils/adminSession'
import { useOrdersHidden } from './hooks/useOrdersHidden'

/**
 * The /orders route, which exists for some shops and not others.
 *
 * Hidden under the eBay hub and shown under shopify and two_way, so it cannot
 * be answered by RouteGuard: that guard has no shop context, and whether a
 * path is routable is not a per-shop question in the first place. It has to be
 * answered here, by the route, because the nav entries alone are not enough —
 * /orders stays typeable and bookmarked, and RouteGuard lets any path through
 * on first render while embedded, since Shopify owns the URL there and
 * navigating away would lose the host and shop params. That is the shape of
 * the problem /pricing already had: a route still reachable by URL is an
 * unlisted section, not a hidden one.
 *
 * Nothing is rendered until the hub is known. Showing Orders and then
 * redirecting would flash a section meant to be hidden; redirecting first
 * would bounce a two-way seller off a page that is genuinely theirs. The wait
 * is one cached settings read, and no wait at all after the first navigation.
 */
function OrdersRoute({ addToast, shop }) {
  const { hidden, known } = useOrdersHidden(shop)
  if (!known) return null
  if (hidden) return <Navigate to="/dashboard" replace />
  return <Orders addToast={addToast} shop={shop} />
}

// ── Helpers ────────────────────────────────────────────────────
function getUrlParam(key) {
  return new URLSearchParams(window.location.search).get(key)
}
function clearUrlParams() {
  window.history.replaceState({}, '', window.location.pathname)
}
/**
 * The shop, from App Bridge.
 *
 * Shopify puts it there for every embedded load, and it is the one source
 * that needs no storage and cannot be stale. Consulted before the cached
 * copy so that storage is a convenience rather than something the app
 * depends on — which is what lets it work with site data blocked, in
 * incognito, and in an iframe with third-party cookies off.
 *
 * Wrapped because App Bridge may not have initialised yet, and reading
 * through a missing global must not throw during the first render.
 */
function appBridgeShop() {
  try {
    return window.shopify?.config?.shop || ''
  } catch {
    return ''
  }
}

/**
 * Where the shop comes from, in order of how much it can be trusted.
 *
 * The URL parameter is what Shopify just told us, App Bridge is what the
 * embedding says now, and the cached copy is only a convenience for a reload
 * that arrives without either. The cache used to sit second, which made a
 * blocked localStorage look like "no shop" and sent the merchant to the
 * install screen on a shop that was already installed.
 */
function resolveShop(urlShop) {
  // An admin view answers from its own tab, ahead of the two merchant-flow
  // sources beneath it, and this is a correctness fix rather than a
  // convenience. App Bridge is not loaded during an impersonation, so
  // appBridgeShop() is always '' there, and the cached copy is shared by every
  // tab on this origin: two portals open on two sellers leave whichever was
  // opened last in the cache, and a refresh of the other tab then resolves to
  // the wrong seller while the ADMIN VIEW banner still names the right one.
  //
  // Nothing changes for a merchant: the key this reads is only ever written
  // on a load carrying admin_impersonate=1.
  return urlShop || adminImpersonationShop() || appBridgeShop() || getSavedShop()
}

function saveShop(domain) {
  // Best effort. A refused write is not an error: the URL parameter and App
  // Bridge both still answer, so nothing above depends on this sticking.
  //
  // An impersonation does not write it at all. The cache is the merchant
  // flow's, shared across tabs, and letting staff fill it with a seller's
  // domain is what makes the wrong-seller resolution above possible in the
  // first place. The per-tab key captured in utils/adminSession is where an
  // admin view keeps its answer.
  if (domain && !isAdminView()) writeStored('shop_domain', domain)
}
function getSavedShop() {
  return readStored('shop_domain', '') || ''
}
function clearShop() {
  removeStored('shop_domain')
}

/**
 * 'needs_source' when the seller has not yet said which side is the hub, else
 * 'ready'.
 *
 * Only a shop that has never answered is asked. Shops installed before the
 * question existed are marked as answered, so they go straight through and
 * nothing about them changes.
 *
 * A failure here returns 'ready'. Being unable to read one setting is no reason
 * to hold someone on an onboarding screen they cannot get past.
 */
/**
 * Where a seller actually is in onboarding — for the ADMIN VIEW banner only.
 *
 * The admin branch of init deliberately skips onboarding and goes straight to
 * 'ready', so staff can reach any screen of a shop that has not finished
 * signing up. The cost is that the admin view tells you nothing about where the
 * seller is stuck, which is usually the reason you opened their portal. Three
 * review stores showed a working dashboard while every one of them was in fact
 * still sitting on the eBay step.
 *
 * So this reads the same two endpoints the seller's own init reads, in the same
 * order, and reports a label instead of changing where anyone is sent. It is
 * read-only, it never sets `status`, and nothing waits on it: a failure leaves
 * the banner without a step rather than holding up the portal.
 *
 * The seller's own path is untouched — this is a second, parallel read that
 * exists only while impersonating.
 */
async function readSellerStep(domain) {
  // The labels the seller sees on their own progress bar, from
  // pages/OnboardingFlow: ['eBay', 'Location', 'Source', 'Plan'].
  try {
    // `resp` deliberately, matching the seller path: __smoke__/session asserts
    // that every use of this endpoint checks resp.ok BEFORE reading the body,
    // because a 401 read as `connected: false` once sent working shops back to
    // the connect-eBay step.
    const resp = await authedFetch(`/api/ebay-oauth/status?shop=${domain}`)
    if (!resp.ok) return null
    const d = await resp.json()
    if (!d?.connected) return { step: 1, of: 4, label: 'eBay', detail: 'has not connected eBay yet' }

    const s = await authedFetch(`/api/settings?shop=${domain}`).then(x => x.json())
    // `needs_plan` in the seller's own state machine, but what it actually
    // gates on is the eBay location — so it is named here as the seller sees
    // it rather than as the status string spells it.
    if (!s?.ebay?.locationConfigured) {
      return { step: 2, of: 4, label: 'Location', detail: 'eBay location not set' }
    }
    if (s?.sync?.syncSourceChosen === false) {
      return { step: 3, of: 4, label: 'Source', detail: 'has not chosen a hub' }
    }
    return { step: 4, of: 4, label: 'Done', detail: 'onboarding complete' }
  } catch {
    return null
  }
}

async function readyOrNeedsSource(domain) {
  try {
    const r = await authedFetch(`/api/settings?shop=${domain}`)
    const d = await r.json()
    return d?.sync?.syncSourceChosen === false ? 'needs_source' : 'ready'
  } catch {
    return 'ready'
  }
}

export default function App() {
  // status: 'checking' | 'needs_shopify' | 'needs_ebay' | 'needs_plan'
  //       | 'needs_source' | 'ready'
  const [status,   setStatus]  = useState('checking')
  // Admin view only: where the seller actually is in onboarding, read without
  // changing where anyone is sent. Null until it answers, and null if it
  // cannot — the banner simply omits the step.
  const [sellerStep, setSellerStep] = useState(null)
  const [shop,     setShop]    = useState('')
  const [ebayName, seteBayName] = useState('')
  const { toasts, addToast, dismiss } = useToast()
  useLocation() // keep for future use
  const navigate = useNavigate()
  // Passed to the dashboard so it can start the first product import itself
  // rather than leaving the seller on an empty screen.
  const [justOnboarded, setJustOnboarded] = useState(false)

  useEffect(() => {
    init()
  }, [])

  async function init() {
    const params       = new URLSearchParams(window.location.search)
    const ebayConnected  = params.get('ebay_connected')
    const shopParam      = params.get('shop')
    const shopifyError   = params.get('shopify_error')
    const ebayError      = params.get('ebay_error')
    const justSubscribed = params.get('subscribed')

    // ── Admin impersonation — skip all auth, go straight to app ──
    //
    // Both halves used to be read from the url, and the url keeps neither.
    // The first navigation strips the query, so a refresh arrived with no
    // parameter and no shop, fell through into the Shopify auth flow below,
    // and drew the install gate at staff who were already authenticated by
    // the admin panel's own login.
    //
    // Both now come from the session, captured at import in
    // utils/adminSession: isAdminView is the token-aware question, and the
    // shop is the per-tab copy rather than the cache shared across tabs.
    // shopParam still wins when it is there, which is the opening load.
    const impersonatedShop = shopParam || adminImpersonationShop()
    if (isAdminView() && impersonatedShop) {
      setShop(impersonatedShop)
      setStatus('ready')
      // Read where the seller actually is, for the banner. Deliberately not
      // awaited: the portal opens immediately and the step appears a moment
      // later, because staff should not wait on it and a failure to read it
      // must not stop them getting in.
      readSellerStep(impersonatedShop).then(setSellerStep).catch(() => {})
      return
    }

    // ── Handle errors coming back from OAuth ───────────────────
    if (shopifyError) {
      addToast('error', decodeURIComponent(shopifyError))
      clearUrlParams()
    }
    if (ebayError) {
      addToast('error', decodeURIComponent(ebayError))
      clearUrlParams()
    }

    // ── Just subscribed to a plan ─────────────────────────────
    if (justSubscribed === '1') {
      clearUrlParams()
      const domain = resolveShop(shopParam)
      if (domain) {
        saveShop(domain)
        setShop(domain)
        addToast('success', 'Plan activated! Welcome to SyncE Bridge.')
        // Not necessarily finished: the plan is not the last step. Declaring
        // ready here sent everyone who paid straight to the dashboard, past the
        // source question — so the sellers most worth asking were the ones
        // never asked.
        setStatus(await readyOrNeedsSource(domain))
      } else {
        setStatus('needs_shopify')
      }
      return
    }

    // ── eBay just connected ────────────────────────────────────
    if (ebayConnected === '1') {
      const name = params.get('shop_name') || ''
      seteBayName(name)
      clearUrlParams()
      const domain = resolveShop(shopParam)
      if (domain) {
        saveShop(domain)
        setShop(domain)
        addToast('success', `eBay connected: ${name}`)
        // Check billing — show pricing step if no active plan
        try {
          const bResp = await authedFetch(`/api/billing/status?shop=${domain}`)
          const bData = await bResp.json()
          // managedPlan means this shop is not billed through Shopify — the
          // custom app's seller, or one named in BILLING_EXEMPT_SHOPS. The
          // pricing page is hidden for them, so demanding a plan here sent
          // them to a step with nothing on it and no way forward. There is
          // no charge to wait for, so there is nothing to stop them.
          if (bData.isActive || bData.onTrial || bData.managedPlan) {
            setStatus(await readyOrNeedsSource(domain))
          } else {
            setStatus('needs_plan')
          }
        } catch {
          setStatus('needs_plan')
        }
      } else {
        setStatus('needs_shopify')
      }
      return
    }

    // ── Check saved shop ───────────────────────────────────────
    const savedShop = resolveShop(shopParam)
    if (!savedShop) {
      setStatus('needs_shopify')
      return
    }

    // Verify shop exists in DB and is active
    try {
      const resp = await authedFetch(`/api/settings?shop=${savedShop}`)

      // A failed request is not an answer about the shop.
      //
      // Only a 200 can say "this store is not installed". Anything else — an
      // expired session token answering 401, a gateway hiccup, a 500 — comes
      // back as JSON with no `shopify.connected` in it, and reading that as
      // "not installed" threw the seller out of a working app onto the install
      // screen AND erased the saved shop, so the next load had nothing to
      // recover from either. On a shop where tokens expire under load that is a
      // working install repeatedly telling its owner it does not exist.
      //
      // Inside the Shopify iframe the shop is known to be real — Shopify would
      // not have framed us otherwise — so the app keeps it and carries on,
      // which is what the unreachable-backend branch below has always done.
      if (!resp.ok) {
        const inIframe = window.self !== window.top
        if (savedShop && inIframe) {
          saveShop(savedShop)
          setShop(savedShop)
          setStatus('ready')
        } else {
          setStatus('needs_shopify')
        }
        return
      }

      const data = await resp.json()

      if (data?.uninstalled) {
        // App was uninstalled — clear local state, show onboarding
        clearShop()
        setStatus('needs_shopify')
        return
      }

      if (!data?.shopify?.connected) {
        // Shop not in DB — need fresh install
        clearShop()
        setStatus('needs_shopify')
        return
      }

      saveShop(savedShop)
      setShop(savedShop)
      await checkeBayAndSetStatus(savedShop)
    } catch {
      // Backend unreachable — only trust saved shop if we're inside Shopify iframe.
      // Outside Shopify (direct URL visit), always show onboarding to force reinstall.
      const inIframe = window.self !== window.top
      if (savedShop && inIframe) {
        setShop(savedShop)
        setStatus('ready')
      } else {
        setStatus('needs_shopify')
      }
    }
  }

  async function checkeBayAndSetStatus(domain) {
    setShop(domain)
    try {
      const resp = await authedFetch(`/api/ebay-oauth/status?shop=${domain}`)
      // Same rule as the shop check above: a request that failed is not eBay
      // saying it is disconnected. A 401 from an expired session token used to
      // read as `connected: false` and drop a working shop back to the connect-
      // eBay step, which the navigation guard then refuses to let them leave —
      // so the seller could not reach their products either.
      //
      // 'ready' rather than 'needs_ebay' because the alternative is trapping
      // someone whose eBay is connected on a screen telling them it is not.
      // The inner catch below already chose that for the same reason.
      if (!resp.ok) { setStatus('ready'); return }
      const data = await resp.json()
      if (data.connected) {
        seteBayName(data.shopName || '')
        // Gate on the eBay location, not billing. Pricing is hidden during
        // onboarding, so gating on an active plan sent every merchant back to
        // the location step on every refresh, forever.
        try {
          const sResp = await authedFetch(`/api/settings?shop=${domain}`)
          const sData = await sResp.json()
          if (!sData?.ebay?.locationConfigured) setStatus('needs_plan')
          else setStatus(sData?.sync?.syncSourceChosen === false ? 'needs_source' : 'ready')
        } catch {
          // Cannot tell — let them into the app rather than trapping them.
          setStatus('ready')
        }
      } else {
        setStatus('needs_ebay')
      }
    } catch {
      setStatus('needs_ebay')
    }
  }

  function handleOnboardComplete(info) {
    const domain = info.shopDomain || shop
    if (info.shopDomain) {
      saveShop(info.shopDomain)
      setShop(info.shopDomain)
    }
    if (info.ebayName) seteBayName(info.ebayName)
    setStatus('ready')
    addToast('success', 'Setup complete! Welcome to SyncE Bridge.')

    // The dashboard, not wherever the router happened to be. Onboarding ended on
    // the products list, which is the one screen that says nothing until the
    // import it kicks off has finished.
    setJustOnboarded(true)
    navigate('/dashboard', { replace: true })

    // Read the Shopify catalogue, once, before anything else needs it.
    //
    // Nothing used to. The only call made here linked eBay listings to
    // products already in the app, and a Shopify-hub shop has none at this
    // point — so the seller landed on the dashboard and found it empty. The
    // scheduled pass would have filled it at 25 products every 30 minutes,
    // which is ten hours for a 500-product catalogue and half an hour before
    // the first one shows.
    //
    // Not awaited, and deliberately before the linking below: the import is
    // what gives linking something to match against. The server is
    // idempotent about it, so a seller who arrives twice does not import
    // twice, and an eBay-hub shop is answered "not_shopify_source" and left
    // to its own bootstrap.
    if (domain) {
      apiFetch('POST', '/products/shopify-source/bootstrap', domain)
        .then(d => {
          if (d?.imported) {
            addToast('success',
              `Imported ${d.imported} product${d.imported === 1 ? '' : 's'} from Shopify`)
          }
          if (d?.error) addToast('warning', d.error)
        })
        // Quiet: the scheduled pass imports anyway, and the Products screen
        // has an Import button. A failure here is slower, not broken.
        .catch(() => {})
    }

    // Read eBay and link what is beyond doubt, once, without being asked.
    // A shop that has just connected eBay almost always has listings already,
    // and until they are linked the app cannot update any of them. Only the
    // confident matches are written — see POST /products/link/auto.
    if (domain) {
      apiFetch('POST', '/products/link/auto', domain)
        .then(d => {
          if (d?.linked) {
            addToast('success',
              `Linked ${d.linked} eBay listing${d.linked === 1 ? '' : 's'} to your products`)
          }
          const left = (d?.ambiguous || 0) + (d?.unmatched || 0)
          if (left) {
            addToast('warning',
              `${left} eBay listing${left === 1 ? ' needs' : 's need'} a decision — `
              + 'see Products → Not linked')
          }
        })
        .catch(() => { /* the tab is there to do it by hand */ })
    }
  }

  // ── Checking ───────────────────────────────────────────────
  if (status === 'checking') {
    return (
      <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg)' }}>
        <div style={{ textAlign:'center', color:'var(--text3)' }}>
          <div style={{ fontSize:32, marginBottom:12, animation:'spin 1s linear infinite', display:'inline-block' }}>↺</div>
          <div style={{ fontSize:13, fontFamily:'var(--mono)' }}>Loading SyncE Bridge…</div>
        </div>
      </div>
    )
  }

  // ── Onboarding ─────────────────────────────────────────────
  if (status === 'needs_shopify' || status === 'needs_ebay' || status === 'needs_plan'
      || status === 'needs_source') {
    // needs_plan lands on the location step first — eBay cannot publish without
    // a merchant location, so it is collected before the plan is chosen.
    // 3, not 4: the source step moved ahead of the plan, because which side
    // the seller works from decides which plans apply to them at all.
    const startStep = status === 'needs_source' ? 3
                    : status === 'needs_plan'   ? 2
                    : status === 'needs_ebay'   ? 1 : 0
    return (
      <>
        <OnboardingFlow
          startStep={startStep}
          shop={shop}
          onComplete={handleOnboardComplete}
        />
        <ToastContainer toasts={toasts} dismiss={dismiss} />
      </>
    )
  }

  // ── Main app ───────────────────────────────────────────────
  // Token-aware, not url-only: the parameter is stripped by the first
  // navigation, and reading it alone made the ADMIN VIEW banner — and the
  // padding reserved for it — disappear as soon as staff moved off the
  // dashboard, leaving them in a seller's app with no sign of whose it was.
  const adminView = isAdminView()

  // Opened outside Shopify, this app cannot work: the API answers only to a
  // session token App Bridge mints, so every screen would render empty. Say so
  // rather than showing a shell that looks broken. The legal pages, admin
  // impersonation and the admin panel itself are let through — see
  // utils/embedded for why each one.
  const embedded = isEmbeddedContext({
    host:     INITIAL_HOST,
    pathname: window.location.pathname,
    search:   window.location.search,
    inIframe: (() => { try { return window.self !== window.top } catch { return true } })(),
  })
  if (!embedded) return <OpenInShopify/>

  return (
    <LangProvider>
    <NavigationProvider>
      {/* Register nav items under app name in Shopify Admin sidebar.
          INITIAL_HOST is captured once at page load — stable even after
          React Router navigates and strips the ?host= query param. */}
      {INITIAL_HOST && !adminView && <ShopifyNavMenu shop={shop} />}
      {adminView && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999,
          height: 44, background: '#1a0a00', borderBottom: '2px solid #0064d2',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ background: '#0064d2', color: '#fff', fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 4, letterSpacing: '.08em' }}>ADMIN VIEW</span>
            <span style={{ color: '#f5a623', fontSize: 13, fontWeight: 600 }}>Viewing: {shop}</span>
            {/* Where the seller actually is. The admin view skips onboarding,
                so without this a shop still stuck on the eBay step looks
                identical to a finished one — which is how three review stores
                showed a working dashboard while none of them had connected
                eBay. Green once they are through, amber while they are not. */}
            {sellerStep && (
              <span
                title={`Onboarding: ${sellerStep.detail}`}
                style={{
                  fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 4,
                  border: '1px solid',
                  ...(sellerStep.step === sellerStep.of
                    ? { color: '#2ecc71', borderColor: 'rgba(46,204,113,.45)', background: 'rgba(46,204,113,.12)' }
                    : { color: '#f5a623', borderColor: 'rgba(245,166,35,.45)', background: 'rgba(245,166,35,.12)' }),
                }}>
                {sellerStep.step === sellerStep.of
                  ? 'Onboarded'
                  : `Step ${sellerStep.step}/${sellerStep.of} · ${sellerStep.label} — ${sellerStep.detail}`}
              </span>
            )}
          </div>
          <button onClick={() => window.close()} style={{
            background: 'rgba(0,100,210,0.15)', border: '1px solid rgba(0,100,210,0.4)',
            color: '#0064d2', borderRadius: 8, padding: '5px 14px',
            fontSize: 12, fontWeight: 700, cursor: 'pointer',
          }}>✕ Close</button>
        </div>
      )}
      <Layout addToast={addToast} shop={shop} style={adminView ? { paddingTop: 44 } : {}}>
        <ErrorBoundary>
          <RouteGuard>
            {/* A lazy route is still being fetched for a moment, and React
                needs something to show meanwhile. Deliberately quiet: a page
                that flashes a spinner on every navigation feels slower than
                one that waits a beat. */}
            <Suspense fallback={<div style={{ padding: 24, fontSize: 13, color: 'var(--text3)' }}>Loading…</div>}>
            <Routes>
              {/* When embedded, Shopify controls the URL — render Dashboard at root
                  instead of navigating (navigation drops host/shop params and triggers
                  a Shopify Admin 404 when it detects the iframe URL change). */}
              <Route path="/"          element={<Dashboard  addToast={addToast} shop={shop} justOnboarded={justOnboarded} />} />
              <Route path="/dashboard" element={<Dashboard  addToast={addToast} shop={shop} justOnboarded={justOnboarded} />} />
              <Route path="/products"  element={<Products   addToast={addToast} shop={shop} />} />
              <Route path="/products/:id" element={<ProductDetail addToast={addToast} shop={shop} />} />
              {/* Orders is the shop's own, except under the eBay hub — see
                  OrdersRoute below for why the route decides this and not
                  RouteGuard. */}
              <Route path="/orders"    element={<OrdersRoute addToast={addToast} shop={shop} />} />
              <Route path="/profiling" element={<Profiling  addToast={addToast} shop={shop} />} />
              {/* The rule editor is a page, not a dialog: seven tabs of form
                  do not fit in a modal on a narrow screen. */}
              <Route path="/upload"         element={<UploadFlow addToast={addToast} shop={shop} />} />
              <Route path="/profiling/new"  element={<ProfilingEditor addToast={addToast} shop={shop} />} />
              <Route path="/profiling/:id"  element={<ProfilingEditor addToast={addToast} shop={shop} />} />
              <Route path="/activity"  element={<Activity   addToast={addToast} shop={shop} />} />
              <Route path="/pricing"   element={<Pricing    addToast={addToast} shop={shop} />} />
              <Route path="/settings"  element={<Settings   addToast={addToast} shop={shop} />} />
              {/* No /templates route: every template is created inside the
                  listing rule that uses it, so a separate section was a second
                  place to do the same thing. An old bookmark lands on the rules
                  list rather than a 404. */}
              <Route path="/templates" element={<Navigate to="/profiling" replace />} />
              {/* shop, so the Fetch Orders walkthrough can be hidden for an
                  eBay-hub shop along with the section it describes. */}
              <Route path="/help"      element={<Help shop={shop} />} />
              <Route path="/privacy"   element={<PrivacyPolicy />} />
              <Route path="/terms"     element={<TermsOfService />} />
              <Route path="*"          element={<NotFound />} />
            </Routes>
            </Suspense>
          </RouteGuard>
        </ErrorBoundary>
      </Layout>
      <ToastContainer toasts={toasts} dismiss={dismiss} />
      <TawkChat shop={shop} isAdminView={adminView} />
    </NavigationProvider>
    </LangProvider>
  )
}
